import { updateAccount } from 'admin/actions/account';
import { getPayboxesOnShop, getPayregisOnShop, getPayspotsOnShop } from 'admin/actions/devices';
import { showShop } from 'admin/actions/shops';
import { fetchUserTags, UserTag } from 'admin/actions/user-tags';
import { organizationManagerAllowed } from 'admin/authorities';
import ToggleSwitch from 'admin/components/atoms/ToggleSwitch';
import Breadcrumb from 'admin/components/shared/Breadcrumb';
import Button from 'admin/components/shared/Button';
import TableHeader from 'admin/components/shared/TableHeaders';
import TableRows from 'admin/components/shared/TableRows';
import { EXCLUDED_ISSUER_ORG_CODE } from 'admin/constants/userTagGroupLabels';
import { usageTypeLabel } from 'admin/constants/usageType';
import { useAppDispatch, useAppSelector } from '~/javascripts/admin/hooks/useApp';
import {
  selectUserPrivateMoney,
  selectShowMerchantApplication,
} from '~/javascripts/admin/reducers/user-private-money';
import { canCreateShop } from 'admin/routes';
import { toQueryParams } from 'admin/utils';
import React, { useEffect, useState } from 'react';
import { Link, RouteComponentProps } from 'react-router-dom';
import { createBadge } from '../../atoms/Badge';
import { Account, Shop as ShopType } from 'admin/reducers/shop';
import { Payregi, Paybox, Payspot } from 'admin/reducers/device';
import styles from './styles.scss';

const HeadOfficeBadge = createBadge(
  {
    isHeadOffice: { label: '本店', bgColor: '#3995FF', isRounded: false },
  },
  {
    styles: {
      borderRadius: '9999px',
      padding: '2px 12px',
      textAlign: 'center',
    },
  }
);

const Shop = ({ match, history }: RouteComponentProps<{ id: string }>) => {
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.user);
  const selectedPrivateMoney = useAppSelector(selectUserPrivateMoney);
  // 加盟店 申込フローの表示可否 (選択中マネーの発行体設定に追従)
  const showMerchantApplication = useAppSelector(selectShowMerchantApplication);
  const payregis = useAppSelector(
    (state) => state.device.payregis as { rows: Payregi[]; count: number; pagination: object }
  );
  const payboxes = useAppSelector(
    (state) => state.device.payboxes as { rows: Paybox[]; count: number; pagination: object }
  );
  const payspots = useAppSelector(
    (state) => state.device.payspots as { rows: Payspot[]; count: number; pagination: object }
  );
  const [shop, setShop] = useState<Partial<ShopType>>({});
  const [userTags, setUserTags] = useState<UserTag[]>([]);

  const fetchShop = (shopId?: string) => {
    const id = shopId ?? match.params.id;
    const query = toQueryParams({ withDisabled: true });
    showShop(id, query).then((s) => {
      setShop(s);
      // 加盟組織ユーザでも自分の店舗のタグが取得できるよう、発行体マネーの id を private_money_id で指定する
      // (サーバ側の listing は private_money_id 未指定だと admin user の所属組織でフィルタするため空になる)
      const issuerAccount = (s.accounts || []).find(
        (a: Account) =>
          a.private_money?.organization?.code &&
          a.private_money.organization.code !== EXCLUDED_ISSUER_ORG_CODE
      );
      const tagsQuery = issuerAccount?.private_money?.id
        ? `private_money_id=${issuerAccount.private_money.id}`
        : '';
      fetchUserTags(id, tagsQuery)
        .then((res) => setUserTags(res.rows))
        .catch(() => setUserTags([]));
    });
  };

  useEffect(() => {
    const { id } = match.params;
    fetchShop(id);
    dispatch(getPayspotsOnShop(id));
    dispatch(getPayregisOnShop(id));
    dispatch(getPayboxesOnShop(id));
  }, [match.params.id]);

  // 付与済みタグをグループ単位に集約（multi値グループは複数itemを列挙）。
  // グループ or item が無効化(is_active=false)されている場合は「無効」表示する。
  const assignedTagGroups = Object.values(
    userTags.reduce<
      Record<
        string,
        {
          id: string;
          name: string;
          items: { id: string; name: string; subgroupName: string | null; isActive: boolean }[];
        }
      >
    >((acc, t) => {
      const groupId = t.user_tag_group.id;
      if (!acc[groupId]) {
        acc[groupId] = {
          id: groupId,
          name: t.user_tag_group.name_ja || t.user_tag_group.name_en,
          items: [],
        };
      }
      // 削除済み（is_active=false）の subgroup を指す item は未分類扱いにする。
      const subgroup = t.user_tag_group_item.user_tag_subgroup;
      acc[groupId].items.push({
        id: t.user_tag_group_item.id,
        name: t.user_tag_group_item.name_ja || t.user_tag_group_item.name_en,
        subgroupName:
          subgroup && subgroup.is_active ? subgroup.name_ja || subgroup.name_en : null,
        // グループ無効化（連動削除）または item 単体の無効化のどちらも「無効」とみなす
        isActive: t.user_tag_group.is_active && t.user_tag_group_item.is_active,
      });
      return acc;
    }, {})
  );

  const renderCanTransferTopup = (account: Account) => {
    const handleChange = (checked: boolean) => {
      updateAccount(account.id, { can_transfer_topup: checked }).then(() => {
        fetchShop();
      });
    };
    if (
      organizationManagerAllowed.indexOf(user.role) !== -1 &&
      user.isIssuerOfMoney(account.private_money)
    ) {
      return (
        <ToggleSwitch
          key={account.id}
          checked={account.can_transfer_topup}
          onChange={handleChange}
        />
      );
    }
    return <span>{account.can_transfer_topup ? '有効' : '無効'}</span>;
  };

  const crumb = [
    { value: 'ホーム', href: '/', active: false },
    { value: '店舗管理', href: '', active: true },
    { value: '店舗一覧', href: '/shops', active: false },
    { value: '店舗詳細', href: '', active: true },
  ];

  const accountColumns = [
    { label: <span>マネー名</span>, align: 'left', size: 1 },
    { label: <span>チャージ</span>, align: 'left', size: 2 },
  ];

  const accountRows = (shop.accounts ?? []).map((account) => [
    { data: <span>{account.private_money.name}</span>, align: 'left' },
    { data: renderCanTransferTopup(account), align: 'left' },
  ]);

  const deviceColumns = [
    { label: 'デバイス種別', align: 'left', size: 1 },
    { label: '製造番号', align: 'left', size: 1 },
    { label: 'ハードウェアver', align: 'left', size: 1 },
    { label: '対応マネー', align: 'left', size: 1 },
    { label: 'チャージ', align: 'left', size: 1 },
  ];

  const payregiRows = payregis.rows.map((row) => [
    { data: <span>ペイレジ</span>, align: 'left' },
    {
      data: <Link to={`/settings/devices/payregi/${row.id}`}>{row.serial_number}</Link>,
      align: 'left',
    },
    { data: <span>{row.pcb_ver}</span>, align: 'left' },
    { data: <span>{row.money_name}</span>, align: 'left' },
    { data: <span>{row.topup_enabled ? '可' : '不可'}</span>, align: 'left' },
  ]);

  const payboxRows = payboxes.rows.map((row) => [
    { data: <span>ペイボックス</span>, align: 'left' },
    {
      data: <Link to={`/settings/devices/paybox/${row.id}`}>{row.serial_number}</Link>,
      align: 'left',
    },
    { data: <span>{row.hardware_ver}</span>, align: 'left' },
    { data: <span>{row.money_name}</span>, align: 'left' },
    { data: <span>{row.topup_enabled ? '可' : '不可'}</span>, align: 'left' },
  ]);

  const payspotRows = payspots.rows.map((row) => [
    { data: <span>ペイスポット</span>, align: 'left' },
    {
      data: <Link to={`/settings/devices/payspot/${row.id}`}>{row.serial_number}</Link>,
      align: 'left',
    },
    { data: <span>{row.hardware_ver}</span>, align: 'left' },
    { data: <span>{row.money_name}</span>, align: 'left' },
    { data: <span>{row.topup_enabled ? '可' : '不可'}</span>, align: 'left' },
  ]);

  const memberMoneyNames = (shop.accounts ?? [])
    .filter((a) => a.is_suspended === false)
    .map((a) => a.private_money.name)
    .join(', ');

  // 編集ボタンの表示条件:
  // - canEdit: 編集権限を持つロール
  // - isMerchantUser: 加盟事業者ユーザー（発行体マネーの発行体ではない）
  //   → 編集は申請フロー (/application) 経由のみ。申込フロー非利用の発行体では申請管理画面群を隠しているため、入口の本ボタンも隠す
  // - 発行体ユーザーは直接編集フロー (/edit) で申込フロー設定の影響を受けない
  const canEdit = canCreateShop.includes(user.role);
  const isMerchantUser =
    !!selectedPrivateMoney && !user.isIssuerOfMoney?.(selectedPrivateMoney);
  const showApplicationEditButton = canEdit && isMerchantUser && showMerchantApplication;
  const showDirectEditButton = canEdit && !isMerchantUser;

  return (
    <div>
      <div>
        <Breadcrumb items={crumb} />
        <div className={styles.headline}>店舗詳細</div>
        <div className={styles.row}>
          <div className={styles.label}>店舗ID</div>
          <div className={styles['form-item']}>{shop.id}</div>
        </div>
        <div className={styles.row}>
          <div className={styles.label}>加盟するマネー</div>
          <div className={styles['form-item']}>{memberMoneyNames}</div>
        </div>
        <div className={styles.row}>
          <div className={styles.label}>
            店舗名
            {shop.is_head_office && (
              <span style={{ marginLeft: '16px' }}>
                <HeadOfficeBadge status="isHeadOffice" />
              </span>
            )}
          </div>
          <div className={styles['form-item']}>{shop.name}</div>
        </div>
        <div className={styles.row}>
          <div className={styles.label}>店舗名（フリガナ）</div>
          <div className={styles['form-item']}>{shop.shop_name_furigana}</div>
        </div>
        <div className={styles.row}>
          <div className={styles.label}>組織名</div>
          <div className={styles['form-item']}>{shop.organization?.name}</div>
        </div>
        <div className={styles.row}>
          <div className={styles.label}>組織コード</div>
          <div className={styles['form-item']}>{shop.organization?.code}</div>
        </div>
        <div className={styles.row}>
          <div className={styles.label}>郵便番号</div>
          <div className={styles['form-item']}>{shop.postal_code}</div>
        </div>
        <div className={styles.row}>
          <div className={styles.label}>住所</div>
          <div className={styles['form-item']}>{shop.address}</div>
        </div>
        <div className={styles.row}>
          <div className={styles.label}>電話番号</div>
          <div className={styles['form-item']}>{shop.tel}</div>
        </div>
        <div className={styles.row}>
          <div className={styles.label}>メールアドレス</div>
          <div className={styles['form-item']}>{shop.email}</div>
        </div>
        <div className={styles.row}>
          <div className={styles.label}>担当者名</div>
          <div className={styles['form-item']}>{shop.contact_person_name}</div>
        </div>
        <div className={styles.row}>
          <div className={styles.label}>担当者名（フリガナ）</div>
          <div className={styles['form-item']}>{shop.contact_person_name_furigana}</div>
        </div>
        <div className={styles.row}>
          <div className={styles.label}>外部ID</div>
          <div className={styles['form-item']}>{shop.external_id}</div>
        </div>
        <div className={styles.row}>
          <div className={styles.label}>店舗形態</div>
          <div className={styles['form-item']}>{usageTypeLabel(shop.usage_type)}</div>
        </div>
        <div className={styles.row}>
          <div className={styles.label}>状態</div>
          <div className={styles['form-item']}>{shop.status === 'active' ? '有効' : '無効'}</div>
        </div>
        <div className={styles['section-title']}>店舗ラベル</div>
        {assignedTagGroups.length === 0 ? (
          <div className={styles.row}>
            <div className={styles['form-item']}>(ラベルなし)</div>
          </div>
        ) : (
          assignedTagGroups.map((group) => (
            <div className={styles.row} key={group.id}>
              <div className={styles.label}>{group.name}</div>
              <div className={styles['form-item']}>
                {group.items.map((it, i) => (
                  <span key={it.id} style={it.isActive ? undefined : { color: '#999' }}>
                    {/* 選択フォームと平仄を合わせ、所属 subgroup を「サブグループ名 / ラベル名」で併記 */}
                    {it.subgroupName ? `${it.subgroupName} / ${it.name}` : it.name}
                    {it.isActive ? '' : '（無効）'}
                    {i < group.items.length - 1 ? '、' : ''}
                  </span>
                ))}
              </div>
            </div>
          ))
        )}
        <div className={styles['button-group']}>
          {showApplicationEditButton && (
            <Button onClick={() => history.push(`/shops/${match.params.id}/application`)}>
              編集
            </Button>
          )}
          {showDirectEditButton && (
            <Button onClick={() => history.push(`/shops/${match.params.id}/edit`)}>
              編集
            </Button>
          )}
          {canEdit && (
            <Button onClick={() => history.push(`/shops/${match.params.id}/app`)}>
              アプリ情報編集
            </Button>
          )}
        </div>
      </div>
      <div>
        <div className={styles.headline}>加盟マネー</div>
        <div className={styles['account-list-table']}>
          <TableHeader columns={accountColumns} />
          <TableRows rows={accountRows} columns={accountColumns} />
        </div>
      </div>
      <div>
        <div className={styles.headline}>設置デバイス</div>
        <div>
          <div className={styles['list-table']}>
            <TableHeader columns={deviceColumns} />
            <TableRows rows={payspotRows} columns={deviceColumns} />
            <TableRows rows={payregiRows} columns={deviceColumns} />
            <TableRows rows={payboxRows} columns={deviceColumns} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Shop;
